"use client";

import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from '@/components/icons';
import { CornerRightUp } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { placeBid } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { formatCurrency } from '@/lib/utils';

interface MiniBidFormProps {
  productId: string;
  session: Session | null;
  currentBid: number | null;
  startingBid: number | null;
  buyNowPrice: number | null;
  highestBidderId?: string | null;
  sellerId: string;
  productTitle: string;
  isAuctionActive: boolean;
  onBidSuccess?: () => void; // Optional callback for parent components
}

const MiniBidForm: React.FC<MiniBidFormProps> = ({
  productId,
  session,
  currentBid,
  startingBid,
  buyNowPrice,
  highestBidderId,
  sellerId,
  productTitle,
  isAuctionActive,
  onBidSuccess,
}) => {
  const [bidAmount, setBidAmount] = useState<string>('');
  const [placingBid, setPlacingBid] = useState(false);
  const { toast } = useToast();

  // Add callback for opening avatar sidebar if not logged in
  // This prop should be passed from parent (ProductSidebar)
  const openAvatarSidebar = (window as any).openAvatarSidebar || (() => {});

  const currentEffectiveBid = currentBid || startingBid || 0;

  const getMinBid = useCallback((): number => {
    const rawMin = currentEffectiveBid + 100;
    return Math.ceil(rawMin / 100) * 100;
  }, [currentEffectiveBid]);

  const handlePlaceBid = async () => {
    if (!session?.user?.id) {
      toast({ title: "Error", description: "Debes iniciar sesión para pujar.", variant: "destructive" });
      return;
    }
    if (session.user.id === sellerId) {
      toast({ title: "Error", description: "No puedes pujar por tus propios productos.", variant: "destructive" });
      return;
    }

    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: "Error", description: "Introduce una cantidad de puja válida.", variant: "destructive" });
      return;
    }

    // The backend `placeBid` function handles the "Buy Now" logic if amount >= buyNowPrice
    const isBuyNowAction = buyNowPrice && amount >= buyNowPrice;

    if (!isBuyNowAction) {
      if (amount < getMinBid()) {
        toast({ title: "Error", description: `Tu puja debe ser al menos ${formatCurrency(getMinBid())}.`, variant: "destructive" });
        return;
      }
      if (amount % 100 !== 0) {
        toast({ title: "Error", description: "Tu puja debe ser un múltiplo de $100.", variant: "destructive" });
        return;
      }
    }

    setPlacingBid(true);
    try {
      await placeBid(productId, session.user.id, amount);
      toast({
        title: isBuyNowAction ? "¡Compra exitosa!" : "Puja realizada",
        description: isBuyNowAction
          ? `Has comprado "${productTitle}" por ${formatCurrency(amount)}.`
          : `Has pujado ${formatCurrency(amount)} por ${productTitle}.`
      });
      setBidAmount('');
      onBidSuccess?.();
    } catch (error: any) {
      console.error("Error placing bid:", error);
      toast({ title: "Error al pujar", description: error.message || "No se pudo realizar la puja. Inténtalo de nuevo.", variant: "destructive" });
    } finally {
      setPlacingBid(false);
    }
  };

  const handleBidAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    setBidAmount(rawValue);
  };

  const handleIncrementBid = (increment: number) => {
    const currentBidValue = parseFloat(bidAmount) || getMinBid() - 100;
    let newAmount = currentBidValue + increment;
    
    newAmount = Math.max(newAmount, getMinBid());
    newAmount = Math.ceil(newAmount / 100) * 100;
    
    setBidAmount(newAmount.toString());
  };

  const isBuyNowAction = buyNowPrice && parseFloat(bidAmount) >= buyNowPrice;
  // Determine if the current user is the highest bidder
  const isUserLeading = !!(session?.user?.id && highestBidderId && session.user.id === highestBidderId);

  const parsedBidValue = bidAmount ? parseFloat(bidAmount) : 0;

  // Determine basic validity of the typed bid
  const isTypedBidValid = !isNaN(parsedBidValue) && parsedBidValue > 0 && (isBuyNowAction || (parsedBidValue >= getMinBid() && parsedBidValue % 100 === 0));

  // Button state logic:
  // - If the user is currently the highest bidder and hasn't typed a higher bid, show a green inactive button that says "Liderás esta puja".
  // - If the user typed a higher valid bid, show the normal actionable button (Hacer oferta / Comprar Inmediatamente).
  let buttonDisabled = placingBid || !isAuctionActive || !bidAmount || !isTypedBidValid;
  const buttonClassName = 'w-full';
  let desiredButtonText = isBuyNowAction ? 'Comprar Inmediatamente' : 'Hacer oferta';
  const leadingText = 'Liderás esta puja';

  // If not logged in, show CTA to open avatar sidebar
  if (!session) {
    desiredButtonText = 'Ingresa para ofertar';
    buttonDisabled = false;
  }

  // Determine if we should show the leading text
  const shouldShowLeadingText = isUserLeading && (!bidAmount || parsedBidValue <= currentEffectiveBid);

  // We'll manage the displayed text separately so we can trigger color animation after it changes
  const [displayedButtonText, setDisplayedButtonText] = useState<string>(shouldShowLeadingText ? leadingText : desiredButtonText);
  const [animateToLeading, setAnimateToLeading] = useState<boolean>(shouldShowLeadingText);

  // When desired text or leading state changes, update displayed text immediately,
  // then start color animation shortly after so the text change is perceived first.
  useEffect(() => {
    const newText = shouldShowLeadingText ? leadingText : desiredButtonText;
    setDisplayedButtonText(newText);
    const t = setTimeout(() => {
      setAnimateToLeading(shouldShowLeadingText);
    }, 100); // slightly longer so text is clearly visible before color starts
    return () => clearTimeout(t);
  }, [shouldShowLeadingText, desiredButtonText]);

  // Final disabled state depends on computed logic and whether user is leading without typing a higher bid
  if (shouldShowLeadingText) {
    buttonDisabled = true;
  }

  return (
    <div className="space-y-2 p-3 sm:p-3 md:p-3 border rounded-md bg-background max-w-full overflow-hidden">
      {/* Header removed as requested (overplanning) */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <Input
            id="mini-bid-amount"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={bidAmount ? formatCurrency(parseFloat(bidAmount)) : ''}
            onChange={handleBidAmountChange}
            className="w-full text-xs md:text-sm"
            disabled={placingBid}
            placeholder={formatCurrency(getMinBid())}
            aria-label="Monto de la puja"
          />
          <div className="flex gap-1 md:gap-1.5">
            <Button variant="outline" onClick={() => handleIncrementBid(100)} disabled={placingBid} className="flex-1 text-xs md:text-xs py-1 md:py-1.5 flex items-center justify-center gap-1">
              <CornerRightUp className="h-3 w-3" />
              +$100
            </Button>
            <Button variant="outline" onClick={() => handleIncrementBid(1000)} disabled={placingBid} className="flex-1 text-xs md:text-xs py-1 md:py-1.5 flex items-center justify-center gap-1">
              <CornerRightUp className="h-3 w-3" />
              +$1.000
            </Button>
          </div>
        </div>
        <motion.button
          onClick={
            !session
              ? () => openAvatarSidebar()
              : handlePlaceBid
          }
          disabled={buttonDisabled}
          key={displayedButtonText}
          animate={{
            backgroundColor: animateToLeading ? `hsl(var(--success))` : `hsl(var(--primary))`,
            color: animateToLeading ? `hsl(var(--success-foreground))` : `hsl(var(--primary-foreground))`,
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className={`w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs md:text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-9 md:h-9 px-3 md:px-3 py-1.5 md:py-1.5`}
        >
          {placingBid ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isBuyNowAction ? 'Comprando...' : 'Pujando...'}
            </>
          ) : (
            <span>{displayedButtonText}</span>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default MiniBidForm;